import React, { useContext } from 'react';

import { Typography, Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import CloseIcon from '../../assets/icons/CloseIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import { TenantCtx } from '../../containers/CommonLayout/CommonLayout';
import { colorsPalette } from '../../theme';
import { CardWrapper } from '../UI/CardWrapper';
import { ShareButton } from '../UI/ShareButton';
import useStyles from './styles';

export enum LINKED_IN_CERTIFICATE_TYPE {
  Pathway = 'pathway',
  Course = 'course',
}

export const generateLinkedInUrl = (name, certDate, certUrl, linkedInId, certificateType) => {
  const date = new Date(certDate);
  const year = date.getFullYear();
  const month = date.getMonth();

  return `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${name}${
    certificateType === LINKED_IN_CERTIFICATE_TYPE.Pathway ? '+Pathway' : ''
  }&organizationId=${linkedInId}&issueYear=${year}&issueMonth=${month}&certUrl=${certUrl}`;
};

export interface LinkedInModalProps {
  closeModal?: () => void;
  entityName?: string | null;
  resultHash?: string | null;
  passDate?: string | null;
  certUrl?: string | null;
  certificateType?: LINKED_IN_CERTIFICATE_TYPE;
}

const LinkedInModal: React.FC<LinkedInModalProps> = ({
  closeModal,
  entityName,
  passDate,
  resultHash,
  certUrl,
  certificateType = LINKED_IN_CERTIFICATE_TYPE.Pathway,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { tenant } = useContext(TenantCtx);
  const linkedInId = tenant?.linkedin_id;

  const linkedInUrl = generateLinkedInUrl(
    entityName,
    passDate,
    certUrl || `${window?.location?.origin}/share/${resultHash}`,
    linkedInId,
    certificateType,
  );

  return (
    <CardWrapper wrapperClass={classes.wrapper} data-testid="linkedinModal">
      <Box className={classes.headerWrapper}>
        <Typography variant="h2" className={classes.title}>
          {t('page.test.addToLinkedIn')}
        </Typography>
        <CloseIcon
          data-testid="closeLinkedinModal"
          color={colorsPalette.gray}
          className={classes.closeIcon}
          onClick={closeModal}
        />
      </Box>
      <Typography className={classes.description} data-testid="linkedinModalSubtitle">
        <Trans i18nKey="page.test.addToLinkedInDescr">
          Add your certification for <strong>{{ pathwayName: entityName }}</strong> to LinkedIn
          following the steps below.
        </Trans>
      </Typography>
      <ol className={classes.list}>
        <li className={classes.listItem}>
          <Typography className={classes.listCounter}>1</Typography>
          <a
            data-testid="linkedinLink"
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ShareButton wrapperClass={classes.shareButton}>
              <LinkedInIcon color={colorsPalette.white} />
              {t('page.test.linkedIn')}
            </ShareButton>
          </a>
        </li>
        <li className={classes.listItem}>
          <Typography className={classes.listCounter}>2</Typography>
          {t('page.test.credentialExpire')}
        </li>
        <li className={classes.listItem}>
          <Typography className={classes.listCounter}>3</Typography>
          {t('page.test.credentialId')}
        </li>
        <li className={classes.listItem}>
          <Typography className={classes.listCounter}>4</Typography>
          {t('page.test.clickSave')}
        </li>
      </ol>
    </CardWrapper>
  );
};

export default LinkedInModal;
